import React, {useCallback} from 'react';
import {
    prepareMinimumTimeOfDeparture,
    minimumHourMinutesFromDateString,
    dateFromDateString,
    getEstimation,
    getCountryCode,
    formatAddressSingleLine,
} from '../../libs/utils';
import { Screen } from '../Screen';
import { FabButton } from '../Button';
import { Rating } from '../Rating';
import './style.scss';
import {DeliveryStatusBar} from "../Statusbar";
import DeliveryLiveMap from "../../screens/driverLocation";
import {getLatestDriverLocation} from "../../libs/api";

const DeliveryArea = ({
    data,
    onSettingsPress,
    onChatPress,
    onReview,
    onHelpRequest,
    openCaseId,
    onSeeCaseDetails,
    openCaseInfo,
    oldDelivery,
    routeInformation,
    deliveryStatusDetail,
    externalStatus,
    remainingDeliveriesBeforeNumber,
    traveller,
}) => {

    const driver_taking_break = !!(
        Number(traveller?.break_status) === 1 &&
        remainingDeliveriesBeforeNumber !== null &&
        remainingDeliveriesBeforeNumber <= 10
    );

    const regionIsUSA = getCountryCode() === 'us';

    const getLatestExternal = () => {
        if (!Array.isArray(externalStatus) || externalStatus.length === 0) return null;
        return externalStatus
            .filter((e) => e?.status && e?.moment)
            .reduce((latest, cur) => {
                if (!latest) return cur;
                return new Date(cur.moment) > new Date(latest.moment) ? cur : latest;
            }, null);
    };

    const destinationLat = Number(data?.recipient?.address?.latitude)
    const destinationLng = Number(data?.recipient?.address?.longitude)

    const hasDestinationCoordinates =
        Number.isFinite(destinationLat) &&
        Number.isFinite(destinationLng)

    const showLiveMap = !!(
        ['PICKED_UP'].includes(data?.status) &&
        ['to_delivery'].includes(getLatestExternal()?.status) &&
        remainingDeliveriesBeforeNumber != null &&
        remainingDeliveriesBeforeNumber <= 3 &&
        !regionIsUSA &&
        hasDestinationCoordinates
    )

    /**
     * returns delivery date based on the status list detail.
     *
     * @param {Array<{public_id: string, moment: string, status: string}>} statusListDetail - The list of status details.
     * @returns {String|null} - The delivery date or null if not found.
     */
    const getDeliveryDate = (statusListDetail) => {
        if (!statusListDetail || statusListDetail?.length <= 0) {
            return null;
        }
        for (const statusDetail of statusListDetail) {
            if (statusDetail.status === 'DELIVERED') {
                return dateFromDateString(statusDetail.moment);
            }
        }
        return null;
    };

    /**
     * Retrieves the title corresponding to a given delivery status.
     *
     * The function takes a status string as input and returns a user-friendly title
     * representing the current state of delivery based on predefined mapping.
     *
     * @param {string} status - The delivery status. Possible values include:
     *                          'IN_STORE', 'PICKED_UP', 'DELIVERED', 'REVIEWED',
     *                          'CANCELLED', 'TO_RETURN', and 'RETURNED'.
     * @returns {string} A string representing the title corresponding to the delivery status.
     *                   Defaults to 'Planned delivery' if the input status does not match
     *                   any predefined cases.
     */
    const getTitle = (status) => {
        switch (status) {
            case 'IN_STORE':
                return 'Planned delivery'
            case 'PICKED_UP':
                return 'In transit';
            case 'DELIVERED':
            case 'REVIEWED':
                return 'Delivered';
            case 'CANCELLED':
                return 'Canceled';
            case 'TO_RETURN':
                return 'Attempt failed';
            case 'RETURNED':
                return 'In transit';
            default:
                return 'Planned delivery';
        }
    };

    /**
     * Calculates the estimated end time of a delivery based on the provided start time
     * and various conditions such as the day of the week and available route information.
     * Adjusts time differently for weekdays and weekends and considers specific
     * start moments if available.
     *
     * @function
     * @param {Date} deliveryStart - The start time of the delivery.
     * @param {boolean | null} rdi - Indicates whether the recipient is a commercial address.
     * @returns {Date} The estimated end time of the delivery.
     */
    const estimatedEndTime = (deliveryStart, rdi = null) => {
        const deliveryEnd = new Date(deliveryStart);
        const weekDay = deliveryEnd.getDay();

        /*
        // if the address is commercial
        if (rdi !== null && !Boolean(rdi) && routeInformation) {
            deliveryEnd.setHours(routeInformation.commercialHour, 0, 0, 0);
            return deliveryEnd;
        }
        */

        // rdi is null or address not commercial flow
        if (data.start_moment) {
            deliveryEnd.setHours(
                deliveryEnd.getHours(),
                deliveryEnd.getMinutes() + 20,
            );
        } else {
            const endHour = weekDay === 0 || weekDay === 6 ? routeInformation ? (routeInformation.startDeliveryTime + routeInformation.shiftLength): 22 : routeInformation ? (routeInformation.startDeliveryTime + routeInformation.shiftLength): 22;
            deliveryEnd.setHours(endHour, 0);
        }
        return deliveryEnd;
    };

    const mapStatusToStep = (status) => {
        if (externalStatus && externalStatus.length > 0) {
            const lastStatus = externalStatus[externalStatus.length - 1]?.status
            // check externalStatus first for a few statuses only
            switch (lastStatus) {
                case "delivery_reattempt":
                    return 1;
                case "to_return_origin":
                case 'returned_origin':
                case "in_transit_to_return_origin":
                    return 0;
                case "returned":
                    return 1;
                default:
                    break;
            }
        }

        switch (status) {
            case "IN_STORE":
                return 0;

            case "PICKED_UP":
            case "DELIVERING":
            case "TO_RETURN":
            case "RETURNED":
                return 1;

            case "DELIVERED":
            case "REVIEWED":
                return 2;

            case "CANCELLED":
            default:
                return 0;
        }
    };

    const getIssueProps = (status) => {

        // if the driver is taking a break and delivery is picked-up status, show the banner message
        if (driver_taking_break && ["PICKED_UP"].includes(status)) {
            return {
                variant: "issue",
                terminal: false,
                issueLabel: null,
                issueText: "Driver is on a short break and will resume shortly. Your delivery time may shift, and we’ll update your estimate when deliveries resume.",
            };
        }

        if (externalStatus && externalStatus.length > 0) {
            const latestStatus = getLatestExternal();
            const lastStatus = (latestStatus?.status ?? "").toString().trim().toLowerCase();

            // check externalStatus first for a few statuses only
            if (["in_transit_to_return_origin", "to_return_origin"].includes(lastStatus)) {
                return {
                    variant: "issue",
                    terminal: true,
                    issueLabel: null,
                    issueText: "Delivery won’t be completed. The order is being returned to the sender",
                };
            }

            if (["returned_origin"].includes(lastStatus)) {
                return {
                    variant: "issue",
                    terminal: true,
                    issueLabel: null,
                    issueText: "Delivery won’t be completed. This order has been returned to the sender",
                };
            }

            if (['delivery_attempt_failed'].includes(lastStatus)) {
                return {
                    variant: "issue",
                    terminal: false,
                    issueLabel: null,
                    issueText: "Our delivery attempt failed. Your order is returning to our facility to schedule a new delivery attempt",
                };
            }

            if (['delivery_reattempt'].includes(lastStatus)) {
                return {
                    variant: "issue",
                    terminal: false,
                    issueLabel: null,
                    issueText: "A new delivery attempt is scheduled. Tracking has been updated",
                };
            }
        }

        // fallback to normal statuses
        if (status === "TO_RETURN") {
            return {
                variant: "issue",
                terminal: false,
                issueLabel: null,// "Attempt failed",
                issueText: "Our delivery attempt failed. Your order is returning to our facility to schedule a new delivery attempt",
            };
        }
        if (["RETURNED"].includes(status)) {
            return {
                variant: "issue",
                terminal: false,
                issueLabel: null,
                issueText: "Your order is being held at our facility. A new delivery attempt is being scheduled",
            };
        }
        if (status === "CANCELLED") {
            return {
                variant: "issue",
                terminal: true,
                issueLabel: null,
                issueText: "This delivery was canceled",
            };
        }
        return { variant: "normal", terminal: false };
    };

    const determineRemainingDeliveriesBeforeShow = () => {
        if (data && data?.start_moment && ['PICKED_UP'].includes(data?.status) && data?.sequence) {
            return remainingDeliveriesBeforeNumber;
        }
        return null
    }

    const tier = Number(data?.estimation_hour)
    const hasAnchor = (tier === 1 || tier === 2) && data?.estimation_anchor_time
    const windowMinutes = tier === 2 ? 120 : tier === 1 ? 60 : 20

    const deliveryBegin = data?.expected
        ? (
            hasAnchor
                ? new Date(new Date(data.estimation_anchor_time).getTime() - windowMinutes * 60 * 1000)
                : prepareMinimumTimeOfDeparture(
                    new Date(data.expected),
                    data.start_moment,
                    routeInformation ? routeInformation.startDeliveryTime : 15
                )
        )
        : null

    const deliveryEnd = deliveryBegin
        ? (
            hasAnchor
                ? new Date(data.estimation_anchor_time)
                : estimatedEndTime(deliveryBegin, data?.recipient?.rdi)
        )
        : null

    const fetchDriverLocation = useCallback(async () => {
        const latestLocationResponse = await getLatestDriverLocation(data?.travelId)
        const latestLocation = latestLocationResponse?.locationUpdates?.[0]

        if (!latestLocation) return null

        const updatedAt = latestLocation.timestampISO
        const updatedAtDate = updatedAt ? new Date(updatedAt) : null

        const locationIsFresh =
            updatedAtDate &&
            Number.isFinite(updatedAtDate.getTime()) &&
            Date.now() - updatedAtDate.getTime() <= 3 * 60 * 1000

        if (!locationIsFresh) return null

        const lat = Number(latestLocation.latitude || latestLocation.lat)
        const lng = Number(latestLocation.longitude || latestLocation.lng)

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) { return null }

        return {
            lat,
            lng,
            updatedAt,
        }
    }, [data?.travelId])

    return (
        <Screen
            header={
                ['DELIVERED', 'REVIEWED', 'CANCELLED'].indexOf(data.status) < 0 ? (
                    <>
                        <FabButton
                            onClick={onSettingsPress}
                            title="Settings"
                            icon="settings"/>
                            <FabButton
                                onClick={onChatPress}
                                title="Message"
                                icon={(['IN_STORE', 'TO_RETURN', 'RETURNED'].indexOf(data.status) >= 0 || data?.travellerId === null) ? "help" : "chat"} />
                    </>
                ) : null
            }
            openCaseId={openCaseId}
            openCaseInfo={openCaseInfo}
            onSeeCaseDetails={onSeeCaseDetails}
            title={getTitle(data.status)}
            titleExtra={
                <DeliveryStatusBar
                    step={mapStatusToStep(data?.status)}
                    stopsAhead={determineRemainingDeliveriesBeforeShow()}
                     {...getIssueProps(data?.status)}
                />
            }
            footer={
                ['DELIVERED'].indexOf(data.status) >= 0 ? (
                <Rating
                active={data.status === 'DELIVERED'}
                onReview={onReview}
                /> ) : null
            }>
            {['DELIVERED'].indexOf(data.status) < 0 || !data ? (
                data.deliver_date ? (
                    <h2 className="text-title-1 text-title-2@medium-up spaced-24">
                        {dateFromDateString(getEstimation(data.deliver_date))}
                    </h2>
                ) : null
            ) : (
                <h2 className="text-title-1 text-title-2@medium-up spaced-24">
                    {getDeliveryDate(deliveryStatusDetail)}
                </h2>
            )}
            {['DELIVERED', 'REVIEWED', 'CANCELLED', 'TO_RETURN', 'RETURNED'].indexOf(data.status) < 0 && data.expected && deliveryBegin && deliveryEnd ? (
                <h2 className="text-title-1 text-title-2@medium-up">
                    {minimumHourMinutesFromDateString(deliveryBegin, data.start_moment, routeInformation ? routeInformation.startDeliveryTime : 15, false)} -{' '}
                    {minimumHourMinutesFromDateString(deliveryEnd, data.start_moment, routeInformation? routeInformation.startDeliveryTime : 15, true)}
                </h2>
            ) : null}

            {showLiveMap ? (
                <DeliveryLiveMap
                    deliveryId={data.id}
                    destination={{
                        lat: Number(data.recipient.address.latitude),
                        lng: Number(data.recipient.address.longitude),
                    }}
                    initialDriverLocation={null}
                    fetchDriverLocation={fetchDriverLocation}
                />
            ) : null}

            <h3 className="text-title-2 spaced-24 text-headline@medium-up address-title">
                {formatAddressSingleLine(data?.recipient?.address)}
            </h3>
            {!openCaseId && !oldDelivery ? (
                <p
                    onClick={onHelpRequest}
                    style={{
                        paddingTop: 25,
                        color: '#2EB4FF',
                        fontSize: 20,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                    }}>
                    Help with this delivery
                </p>
            ) : null}

            <div className="spaced-16" />
            {/* link to make a payment for this delivery's by redirecting to payment_link  */}
            {data.cod ? (
                !data.payment_collected ? (
                <a
                    href={data.payment_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="PaymentButton">
                    Pay COD amount
                </a>
            ) : (
                <p style={{ color: '#008800', fontWeight: '500' }}>Your payment has been successfully processed.</p>
            ))
            : null}

            <div className="spaced-16"></div>

            {/* {data.status === 'PICKED_UP' ? null : data.status === // <Button title="Track delivery [cta]"></Button>
              'DELIVERING' ? (
                <DeliverForm onDeliveryAccept={onDeliveryAccept} />
            ) : null} */}
        </Screen>
    );
};

export default DeliveryArea;
